<template>
  <div>
    <v-container fluid>
      <v-row dense>
        <v-col cols="2">
          <v-menu v-if="quarterly" transition="scale-transition" offset-y v-model="dateMenu" :close-on-content-click="false">
            <template v-slot:activator="{ on }">
              <v-text-field label="Quarter" hide-details dense readonly v-on="on" v-model="quarterText" outlined append-icon="mdi-menu-down" @click:append="dateMenu=true"/>
            </template>
            <v-date-picker v-model="dateRange" type="month" range no-title @click:month="changeQuarter"/>
          </v-menu>
          <v-menu v-else transition="scale-transition" offset-y v-model="dateMenu">
            <template v-slot:activator="{ on }">
              <v-text-field label="Month" hide-details dense readonly v-on="on" v-model="monthAsDate" outlined append-icon="mdi-menu-down" @click:append="dateMenu=true"/>
            </template>
            <v-date-picker v-model="month" type="month" no-title @change="refresh"/>
          </v-menu>
        </v-col>
        <v-col cols="4">
          <v-autocomplete outlined dense hide-details v-model="view" label="View"
                    :items="[{text:'Container Deposit Statement (LCB2413)',value:0},
                             {text:'Direct Delivery Sales Report (LCB2414)',value:1},
                             {text:'Duty Free Sales Report (LCB2415)',value:2},
                             {text:'Direct Delivery to SOP Sales Report (LCB2416)',value:3},
                             {text:'Remittance Summary',value:4}]"/>
        </v-col>
        <v-col cols="1"/>
        <v-col align-self="center">
          <template v-if="company.permissions.includes('excise.file')">
            <v-btn v-if="(quarterly?dateRange[1]:month)>periodEnd" color="primary" small @click="file=true">Mark as filed</v-btn>
            <span v-else style="cursor:pointer" @click="unfile=true">
              Filed to end of {{periodEnd | formatDate}}
            </span>
            <reports-ontario-l-c-b-o-file v-model="file" @filed="periodEnd=$event"/>
            <reports-ontario-l-c-b-o-unfile v-model="unfile" @filed="periodEnd=$event"/>
          </template>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="print"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 180px)">
      <span v-show="view===0" v-html="lcb2413Report()"/>
      <span v-show="view===1" v-html="lcb2414Report(true,true)"/>
      <span v-show="view===2" v-html="lcb2415Report(true,true)"/>
      <span v-show="view===3" v-html="lcb2416Report(true,true)"/>
      <span v-show="view===4" v-html="remittanceSummary()"/>
    </v-card>
  </div>
</template>

<script>
import {format,add,startOfQuarter,endOfQuarter,parse} from 'date-fns'
import ReportsOntarioLCBOFile from '@/views/provinces/ON/reports/ReportsOntarioLCBOFile'
import ReportsOntarioLCBOUnfile from '@/views/provinces/ON/reports/ReportsOntarioLCBOUnfile'
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsOntarioLCBO',
  mixins: [printHtml],
  components: {ReportsOntarioLCBOFile,ReportsOntarioLCBOUnfile},
  data() {
    return {
      dateMenu: false,
      quarterly: false,
      month: '',
      dateRange: ['2022-01','2022-3'],
      view: 0,
      lcb2413: null,
      lcb2414: null,
      lcb2415: null,
      lcb2416: null,
      file: false,
      unfile: false,
      periodEnd: '',
      totalPayment: 0
    }
  },
  computed: {
    monthAsDate() {
      return this.$formatDate(this.month+'-01', 'MMMM, yyyy');
    },
    rangeDisplay() {
      return this.quarterly ? `${this.$formatDate(this.dateRange[0]+'-01', 'MMMM, yyyy')} - ${this.$formatDate(this.dateRange[1]+'-01', 'MMMM, yyyy')}` : this.$formatDate(this.month+'-01', 'MMMM, yyyy');
    },
    quarterText() {
      return `${this.$formatDate(this.dateRange[0]+'-01', 'MMM')} to ${this.$formatDate(this.dateRange[1]+'-01', 'MMM, yyyy')}`;
    },
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.$emit('name', 'LCBO Distillery Reporting')

    const results = await this.$apiCall(`query ReportsOntarioLCBO {
                                            ontarioPreferences {
                                              lcboPeriodEnd,lcboQuarterly
                                            }
                                          }`);
    if (!results.error) {
      this.periodEnd = results.data.ontarioPreferences.lcboPeriodEnd;
      this.quarterly = results.data.ontarioPreferences.lcboQuarterly;
    }

    const date = new Date();
    if (this.quarterly) {
      const previousQuarter = add(date, {months:-3});
      this.dateRange[0] = format(startOfQuarter(previousQuarter), 'yyyy-MM');
      this.dateRange[1] = format(endOfQuarter(previousQuarter), 'yyyy-MM');
    }
    else {
      this.month = format(add(date, {months:-1}), 'yyyy-MM');
    }
    this.refresh();
  },
  methods: {
    changeQuarter() {
      const date = new Date();
      this.dateRange[0] = format(startOfQuarter(parse(`${this.dateRange[0]}-01`, 'yyyy-MM-dd', date)), 'yyyy-MM');
      this.dateRange[1] = format(endOfQuarter(parse(`${this.dateRange[0]}-01`, 'yyyy-MM-dd', date)), 'yyyy-MM');
      this.refresh();
      this.dateMenu = false;
    },
    async refresh() {
      const results = await this.$apiCall(`query ReportsOntarioLCBO($date:Date!) {
                                             lcb2413(date:$date) {
                                               le100 {other {containers,depositValue},
                                                      subTotal {containers,depositValue}
                                                     },
                                               le630 {other {containers,depositValue},
                                                      subTotal {containers,depositValue}
                                                     },
                                               gt630 {other {containers,depositValue},
                                                      subTotal {containers,depositValue}
                                                     },
                                                total {containers,depositValue}
                                             },
                                             lcb2414(date:$date) {
                                               grandTotal {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W},
                                               licenseesSubTotal {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W},
                                               tiedHouseSubTotal {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W},
                                               byTheGlassSubTotal {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W},
                                               lines {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W}
                                             }
                                             lcb2415(date:$date) {
                                               totals {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O},
                                               lines {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O}
                                             }
                                             lcb2416(date:$date) {
                                               grandTotal {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S},
                                               lines {A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S}
                                             }
                                           }`,
                                           {date:`${this.quarterly ? this.dateRange[0] : this.month}-01`});
      if (!results.error) {
        this.lcb2413 = results.data.lcb2413;
        this.lcb2414 = results.data.lcb2414;
        this.lcb2415 = results.data.lcb2415;
        this.lcb2416 = results.data.lcb2416;
        this.totalPayment = this.$roundNumber(this.lcb2413.total.depositValue) +
                            this.$roundNumber(this.lcb2414.grandTotal.W) +
                            this.$roundNumber(this.lcb2415.totals.O) +
                            this.$roundNumber(this.lcb2416.grandTotal.S);
      }
    },

    lcb2413Report() {
      if (!this.lcb2413)
        return '';
      return `<style>
                @page {size:portrait}
                .lcb2413 {font-family: Roboto, sans-serif; font-size:14px;width:100%;border-spacing:0;border-collapse:collapse}
                .lcb2413 .title {font-weight:700;font-size:18px;text-align:left}
                .lcb2413 .sub-title {text-align:left}
                .lcb2413 tr {
                  height:25px;
                  vertical-align:bottom;
                }
                .lcb2413 td:first-child {
                  width:400px;
                }
                .lcb2413 td:nth-child(2) {
                  width:150px;
                  border-bottom:1px solid #666;
                  text-align:right;
                  padding-right:10px;
                }
                .lcb2413 td:nth-child(3) {
                  width:20px;
                }
                .lcb2413 td:nth-child(4) {
                  width:150px;
                  border-bottom:1px solid #666;
                  padding-right:10px;
                  text-align:right;
                }
                .lcb2413 .header {
                  font-weight:bold;
                  font-size:14px;
                  text-decoration: underline;
                }
                .lcb2413 .header td {
                  text-align:center;
                  border:none;
                }
                .lcb2413 .header td:first-child {
                  text-align:left;
                }
                .lcb2413 .subheader {
                  font-weight:bold;
                  font-size:14px;
                }
                .lcb2413 .subtotal {
                  font-weight:bold;
                  vertical-align:middle;
                }
                .lcb2413 .subtotal td:nth-child(2) {
                  border:1px solid #666;
                }
                .lcb2413 .subtotal td:nth-child(4) {
                  border:1px solid #666;
                }
              </style>
              <table class="lcb2413">
                <thead>
                  <tr>
                    <th colspan="5" class="title">Distillery Container Deposit Statement</th>
                  </tr>
                  <tr>
                    <th colspan="5" class="sub-title">${this.rangeDisplay}</th>
                  </tr>
                  <tr><th colspan="5">&nbsp;</th></tr>
                </thead>
                <tbody>
                  <tr class="header">
                    <td>Deposits Collected by Channel</td>
                    <td>Containers</td>
                    <td></td>
                    <td>Deposit Value</td>
                  </tr>
                  <tr><td colspan="4" class="subheader">Less than or equal to 100 ml ($0.00 deposit)</td></tr>
                  <tr>
                    <td>Other (e.g. drs, promotional, tastings)</td>
                    <td>${this.number(this.lcb2413.le100.other.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.le100.other.depositValue,2)}</td>
                  </tr>
                  <tr>
                    <td>Less: Refilled Containers</td>
                    <td>${this.number(0)}</td>
                    <td></td>
                    <td>${this.number(0,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>
                  <tr class="subtotal">
                    <td>Subtotal</td>
                    <td>${this.number(this.lcb2413.le100.subTotal.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.le100.subTotal.depositValue,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>

                  <tr><td colspan="4" class="subheader">Greater than 100 ml and less than or equal to 630 ml ($0.10 deposit)</td></tr>
                  <tr>
                    <td>Other (e.g. drs, promotional, tastings)</td>
                    <td>${this.number(this.lcb2413.le630.other.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.le630.other.depositValue,2)}</td>
                  </tr>
                  <tr>
                    <td>Less: Refilled Containers</td>
                    <td>${this.number(0)}</td>
                    <td></td>
                    <td>${this.number(0,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>
                  <tr class="subtotal">
                    <td>Subtotal</td>
                    <td>${this.number(this.lcb2413.le630.subTotal.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.le630.subTotal.depositValue,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>

                  <tr><td colspan="4" class="subheader">Greater than 630 ml ($0.20 deposit)</td></tr>
                  <tr>
                    <td>Other (e.g. drs, promotional, tastings)</td>
                    <td>${this.number(this.lcb2413.gt630.other.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.gt630.other.depositValue,2)}</td>
                  </tr>
                  <tr>
                    <td>Less: Refilled Containers</td>
                    <td>${this.number(0)}</td>
                    <td></td>
                    <td>${this.number(0,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>
                  <tr class="subtotal">
                    <td>Subtotal</td>
                    <td>${this.number(this.lcb2413.gt630.subTotal.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.gt630.subTotal.depositValue,2)}</td>
                  </tr>
                  <tr><td colspan="4"></td></tr>

                  <tr class="subtotal subheader">
                    <td>Total Containers and Deposits</td>
                    <td>${this.number(this.lcb2413.total.containers)}</td>
                    <td></td>
                    <td>${this.number(this.lcb2413.total.depositValue,2)}</td>
                  </tr>
                </tbody>
              </table>`
    },

    lcb2414Report(left, right) {
      if (!this.lcb2414)
        return '';

      const colspan = (left?11:0) + (right?12:0);
      const lines = [
              `<style>
                @page {size:landscape}
                .lcb2414 {font-family: Roboto, sans-serif; font-size:10px;width:100%;border-spacing:0;border-collapse:collapse}
                .lcb2414 .title {font-weight:700;font-size:18px;text-align:left;border:none}
                .lcb2414 .sub-title {text-align:left;border:none;font-size:14px}
                .lcb2414-left {text-align:left !important}
                .lcb2414-center {text-align:center !important}
                .lcb2414 tr {
                  height:30px;
                  vertical-align:middle;
                }
                .lcb2414 th {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
                .lcb2414 .total-heading {
                  font-weight:bold;
                  text-align:left;
                }
                .lcb2414 .total-blank {
                  background-color:#b3b3b3;
                }
                .lcb2414 .total-empty {
                  border:none;
                }
                .lcb2414 .letter-header th {
                  font-weight:bold;
                  border-bottom:none;
                  text-align:center;
                }
                .lcb2414 .main-heading th {
                  font-weight:bold;
                  border-top:none;
                  text-align:center;
                }
                .lcb2414 td {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
              </style>
              <table class="lcb2414">
                <thead>
                  <tr>
                    <th colspan="${colspan}" class="title">Distillery Direct Delivery Sales Report</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="title">Summary of Spirit Sales to Licensees on behalf of the LCBO</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="sub-title">${this.rangeDisplay}</th>
                  </tr>
                  <tr><th colspan="${colspan}" class="sub-title">&nbsp;</th></tr>`];
      for (const header of [{title:'Grand total',index:'grandTotal'},{title:'Licensees Subtotal',index:'licenseesSubTotal'},
                            {title:'Tied-House Subtotal',index:'tiedHouseSubTotal'},{title:'By the Glass Subtotal',index:'byTheGlassSubTotal'}]) {
        lines.push(`<tr>`);
        if (left)
          lines.push(`
                  <th class="total-empty"/>
                  <th colspan="3" class="total-heading">${header.title}</th>
                  <th class="total-blank"/>
                  <th>${this.lcb2414[header.index].F}</th>
                  <th class="total-blank"/>
                  <th class="total-blank"/>
                  <th>${this.lcb2414[header.index].I}</th>
                  <th>${this.number(this.lcb2414[header.index].J,3)}</th>
                  <th class="total-blank"/>
                  `);
        if (right)
          lines.push(`
                  <th>${this.currency(this.lcb2414[header.index].L)}</th>
                  <th>${this.currency(this.lcb2414[header.index].M)}</th>
                  <th>${this.currency(this.lcb2414[header.index].N)}</th>
<!--                  <th>${this.currency(this.lcb2414[header.index].O)}</th>  -->
                  <th>${this.currency(this.lcb2414[header.index].P)}</th>
                  <th>${this.currency(this.lcb2414[header.index].Q)}</th>
                  <th>${this.currency(this.lcb2414[header.index].R)}</th>
                  <th>${this.currency(this.lcb2414[header.index].S)}</th>
                  <th>${this.currency(this.lcb2414[header.index].T)}</th>
                  <th>${this.currency(this.lcb2414[header.index].U)}</th>
                  <th>${this.currency(this.lcb2414[header.index].V)}</th>
                  <th>${this.currency(this.lcb2414[header.index].W)}</th>
                  `);
        lines.push(`</tr>`);
      }
      lines.push(`<tr class="letter-header">`);
      if (left)
        lines.push(`
                    <th style="min-width:300px">(A)</th>
                    <th>(B)</th>
                    <th>(C)</th>
                    <th>(D)</th>
                    <th>(E)</th>
                    <th>(F)</th>
                    <th>(G)</th>
                    <th>(H)</th>
                    <th>(I)</th>
                    <th>(J)</th>
                    <th>(K)</th>`);
      if (right)
        lines.push(`
                    <th>(L)</th>
                    <th>(M)</th>
                    <th>(N)</th>
<!--                    <th>(O)</th>  -->
                    <th>(P)</th>
                    <th>(Q)</th>
                    <th>(R)</th>
                    <th>(S)</th>
                    <th>(T)</th>
                    <th>(U)</th>
                    <th>(V)</th>
                    <th>(W)</th>`);
      lines.push(`</tr>
                  <tr class="main-heading">`);
      if (left)
        lines.push(`<th class="lcb2414-left">Product Description</th>
                    <th>Product Alcohol %</th>
                    <th>Product Category</th>
                    <th>Licensee Type</th>
                    <th>Bottle Size (L)</th>
                    <th>Units Sold</th>
                    <th># of Bottles per Unit (Req'd for Product Category 'C')</th>
                    <th>Basic Price per Unit (Retail price less HST and Deposit)</th>
                    <th>Total # of Bottles</th>
                    <th>Total litres</th>
                    <th>Landed Cost</th>`);
      if (right)
        lines.push(`<th>Cost of Goods Sold to LCBO</th>
                    <th>Total Basic Sales</th>
                    <th>15% Discount</th>
                  <!--  <th>10% Licensee Markup</th>       -->
                    <th>Net Sales</th>
                    <th>13% HST on Sales</th>
                    <th>13% HST on COGS</th>
                    <th>Refundable Container Deposit</th>
                    <th>Total Collected</th>
                    <th>Rebate</th>
                    <th>Supplier Portion (excluding container deposit)</th>
                    <th>Total Remittance (excluding container deposit)</th>`);
      lines.push(`</tr>`);
      lines.push(`</thead><tbody>`);
      for (const line of this.lcb2414.lines) {
        lines.push(`<tr>`);
        if (left)
          lines.push(`<td class="lcb2414-left">${line.A}</td>
                      <td>${this.number(line.B,1)}</td>
                      <td class="lcb2414-center">${line.C}</td>
                      <td class="lcb2414-center">${line.D}</td>
                      <td>${this.number(line.E,3)}</td>
                      <td>${line.F}</td>
                      <td>${line.G || ''}</td>
                      <td>${this.currency(line.H)}</td>
                      <td>${line.I}</td>
                      <td>${this.number(line.J,3)}</td>
                      <td>${this.currency(line.K)}</td>`);
        if (right)
          lines.push(`<td>${this.currency(line.L)}</td>
                      <td>${this.currency(line.M)}</td>
                      <td>${this.currency(line.N)}</td>
<!--                      <td>${this.currency(line.O)}</td>  -->
                      <td>${this.currency(line.P)}</td>
                      <td>${this.currency(line.Q)}</td>
                      <td>${this.currency(line.R)}</td>
                      <td>${this.currency(line.S)}</td>
                      <td>${this.currency(line.T)}</td>
                      <td>${this.currency(line.U)}</td>
                      <td>${this.currency(line.V)}</td>
                      <td>${this.currency(line.W)}</td>`);
        lines.push(`</tr>`);
      }
      lines.push(`</tbody></table>`)
      return lines.join('');
    },

    lcb2415Report(left, right) {
      if (!this.lcb2415)
        return '';

      const colspan = (left?7:0) + (right?7:0);
      const lines = [
              `<style>
                @page {size:landscape}
                .lcb2415 {font-family: Roboto, sans-serif; font-size:10px;width:100%;border-spacing:0;border-collapse:collapse}
                .lcb2415 .title {font-weight:700;font-size:18px;text-align:left;border:none}
                .lcb2415 .sub-title {text-align:left;border:none;font-size:14px}
                .lcb2415-left {text-align:left !important}
                .lcb2415-center {text-align:center !important}
                .lcb2415 tr {
                  height:30px;
                  vertical-align:middle;
                }
                .lcb2415 th {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
                .lcb2415 .total-heading {
                  font-weight:bold;
                  text-align:right;
                }
                .lcb2415 .total-blank {
                  background-color:#b3b3b3;
                }
                .lcb2415 .total-empty {
                  border:none;
                }
                .lcb2415 .letter-header th {
                  font-weight:bold;
                  border-bottom:none;
                  text-align:center;
                }
                .lcb2415 .main-heading th {
                  font-weight:bold;
                  border-top:none;
                  text-align:center;
                }
                .lcb2415 td {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
              </style>
              <table class="lcb2415">
                <thead>
                  <tr>
                    <th colspan="${colspan}" class="title">Distillery Duty Free Sales Report</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="title">Summary of Ontario spirit sales made to Duty Free Operators on behalf of the LCBO</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="sub-title">${this.rangeDisplay}</th>
                  </tr>
                  <tr><th colspan="${colspan}" class="sub-title">&nbsp;</th></tr>`];
        lines.push(`<tr>`);
        if (left)
          lines.push(`
                  <th colspan="3" class="total-empty"/>
                  <th class="total-heading">Totals</th>
                  <th class="total-blank"/>
                  <th class="total-blank"/>
                  <th>${this.lcb2415.totals.G}</th>
                  `);
        if (right)
          lines.push(`
                  <th>${this.lcb2415.totals.H}</th>
                  <th class="total-blank"/>
                  <th>${this.number(this.lcb2415.totals.J,3)}</th>
                  <th>${this.currency(this.lcb2415.totals.K)}</th>
                  <th>${this.currency(this.lcb2415.totals.L)}</th>
                  <th>${this.currency(this.lcb2415.totals.M)}</th>
                  <th>${this.currency(this.lcb2415.totals.N)}</th>
                  <th>${this.currency(this.lcb2415.totals.O)}</th>
                  `);
        lines.push(`</tr>`);
      lines.push(`<tr class="letter-header">`);
      if (left)
        lines.push(`
                    <th style="min-width:300px">(A)</th>
                    <th>(B)</th>
                    <th>(C)</th>
                    <th>(D)</th>
                    <th>(E)</th>
                    <th>(F)</th>
                    <th>(G)</th>`);
      if (right)
        lines.push(`<th>(H)</th>
                    <th>(I)</th>
                    <th>(J)</th>
                    <th>(K)</th>
                    <th>(L)</th>
                    <th>(M)</th>
                    <th>(N)</th>
                    <th>(O)</th>`);
      lines.push(`</tr>
                  <tr class="main-heading">`);
      if (left)
        lines.push(`<th class="lcb2415-left">Product Description</th>
                    <th>Product #</th>
                    <th>Product Category</th>
                    <th>Duty Free Operator #</th>
                    <th>Bottle Size (L)</th>
                    <th>Number of Bottles per Selling Unit</th>
                    <th>Selling Units per Case</th>`);
      if (right)
        lines.push(`<th>Number of Cases Sold</th>
                    <th>Cost per Selling Unit</th>
                    <th>Total litres</th>
                    <th>Cost of Goods Sold</th>
                    <th>LCBO  Mark-up</th>
                    <th>Total Collected</th>
                    <th>Unassigned</th>
                    <th>Total Remittance</th>`);
      lines.push(`</tr>`);
      lines.push(`</thead><tbody>`);
      for (const line of this.lcb2415.lines) {
        lines.push(`<tr>`);
        if (left)
          lines.push(`<td class="lcb2415-left">${line.A}</td>
                      <td class="lcb2415-center">${line.B}</td>
                      <td class="lcb2415-center">${line.C}</td>
                      <td class="lcb2415-center">${line.D}</td>
                      <td>${this.number(line.E,3)}</td>
                      <td>${line.F}</td>
                      <td>${line.G}</td>`);
        if (right)
          lines.push(`<td>${line.H}</td>
                      <td>$${this.number(line.I,4)}</td>
                      <td>${this.number(line.J, 3)}</td>
                      <td>${this.currency(line.K)}</td>
                      <td>${this.currency(line.L)}</td>
                      <td>${this.currency(line.M)}</td>
                      <td>${this.currency(line.N)}</td>
                      <td>${this.currency(line.O)}</td>`);
        lines.push(`</tr>`);
      }
      lines.push(`</tbody></table>`)
      return lines.join('');
    },

    lcb2416Report(left, right) {
      if (!this.lcb2416)
        return '';

      const colspan = (left?11:0) + (right?8:0);
      const lines = [
              `<style>
                @page {size:landscape}
                .lcb2416 {font-family: Roboto, sans-serif; font-size:10px;width:100%;border-spacing:0;border-collapse:collapse}
                .lcb2416 .title {font-weight:700;font-size:18px;text-align:left;border:none}
                .lcb2416 .sub-title {text-align:left;border:none;font-size:14px}
                .lcb2416-left {text-align:left !important}
                .lcb2416-center {text-align:center !important}
                .lcb2416 tr {
                  height:30px;
                  vertical-align:middle;
                }
                .lcb2416 th {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
                .lcb2416 .total-heading {
                  font-weight:bold;
                  text-align:left;
                }
                .lcb2416 .total-blank {
                  background-color:#b3b3b3;
                }
                .lcb2416 .total-empty {
                  border:none;
                }
                .lcb2416 .letter-header th {
                  font-weight:bold;
                  border-bottom:none;
                  text-align:center;
                }
                .lcb2416 .main-heading th {
                  font-weight:bold;
                  border-top:none;
                  text-align:center;
                }
                .lcb2416 td {
                  border:1px solid #000;
                  text-align:right;
                  font-weight:normal;
                  padding-left:5px;
                  padding-right:5px;
                  min-width:100px;
                }
              </style>
              <table class="lcb2416">
                <thead>
                  <tr>
                    <th colspan="${colspan}" class="title">Distillery Direct Delivery to SOP Sales Report</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="title">Summary of Spirit Sales to Special Occasion Permit (SOP) Holders on behalf of the LCBO</th>
                  </tr>
                  <tr>
                    <th colspan="${colspan}" class="sub-title">${this.rangeDisplay}</th>
                  </tr>
                  <tr><th colspan="${colspan}" class="sub-title">&nbsp;</th></tr>`];
      lines.push(`<tr>`);
      if (left)
        lines.push(`
                <th class="total-empty"/>
                <th colspan="3" class="total-heading">Grand total</th>
                <th class="total-blank"/>
                <th>${this.lcb2416.grandTotal.F}</th>
                <th class="total-blank"/>
                <th class="total-blank"/>
                <th>${this.lcb2416.grandTotal.I}</th>
                <th>${this.number(this.lcb2416.grandTotal.J,3)}</th>
                <th class="total-blank"/>
                `);
      if (right)
        lines.push(`
                <th>${this.currency(this.lcb2416.grandTotal.L)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.M)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.N)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.O)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.P)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.Q)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.R)}</th>
                <th>${this.currency(this.lcb2416.grandTotal.S)}</th>
                `);
      lines.push(`</tr>`);

      lines.push(`<tr class="letter-header">`);
      if (left)
        lines.push(`
                    <th style="min-width:300px">(A)</th>
                    <th>(B)</th>
                    <th>(C)</th>
                    <th>(D)</th>
                    <th>(E)</th>
                    <th>(F)</th>
                    <th>(G)</th>
                    <th>(H)</th>
                    <th>(I)</th>
                    <th>(J)</th>
                    <th>(K)</th>`);
      if (right)
        lines.push(`
                    <th>(L)</th>
                    <th>(M)</th>
                    <th>(N)</th>
                    <th>(O)</th>
                    <th>(P)</th>
                    <th>(Q)</th>
                    <th>(R)</th>
                    <th>(S)</th>`);
      lines.push(`</tr>
                  <tr class="main-heading">`);
      if (left)
        lines.push(`<th class="lcb2416-left">Product Description</th>
                    <th>Product Alcohol %</th>
                    <th>Product Category</th>
                    <th>SOP License #</th>
                    <th>Bottle Size (L)</th>
                    <th>Units Sold</th>
                    <th># of Bottles per Unit (Req'd for Product Category 'C')</th>
                    <th>Basic Price per Unit (Retail price less HST and Deposit)</th>
                    <th>Total # of Bottles</th>
                    <th>Total litres</th>
                    <th>Landed Cost</th>`);
      if (right)
        lines.push(`<th>Cost of Goods Sold to LCBO</th>
                    <th>Net Sales</th>
                    <th>13% HST on Sales</th>
                    <th>13% HST on COGS</th>
                    <th>Refundable Container Deposit</th>
                    <th>Total Collected</th>
                    <th>Supplier Portion (excluding container deposit)</th>
                    <th>Total Remittance (excluding container deposit)</th>`);
      lines.push(`</tr>`);
      lines.push(`</thead><tbody>`);
      for (const line of this.lcb2416.lines) {
        lines.push(`<tr>`);
        if (left)
          lines.push(`<td class="lcb2416-left">${line.A}</td>
                      <td>${this.number(line.B,1)}</td>
                      <td class="lcb2416-center">${line.C}</td>
                      <td class="lcb2416-center">${line.D}</td>
                      <td>${this.number(line.E,3)}</td>
                      <td>${line.F}</td>
                      <td>${line.G || ''}</td>
                      <td>${this.currency(line.H)}</td>
                      <td>${line.I}</td>
                      <td>${this.number(line.J,3)}</td>
                      <td>${this.currency(line.K)}</td>`);
        if (right)
          lines.push(`<td>${this.currency(line.L)}</td>
                      <td>${this.currency(line.M)}</td>
                      <td>${this.currency(line.N)}</td>
                      <td>${this.currency(line.O)}</td>
                      <td>${this.currency(line.P)}</td>
                      <td>${this.currency(line.Q)}</td>
                      <td>${this.currency(line.R)}</td>
                      <td>${this.currency(line.S)}</td>`);
        lines.push(`</tr>`);
      }
      lines.push(`</tbody></table>`)
      return lines.join('');
    },

    remittanceSummary() {
      if (!this.lcb2413 || !this.lcb2414 || !this.lcb2415 || !this.lcb2416)
        return '';
      return `<style>
                @page {size:portrait}
                .remittance {font-family: Roboto, sans-serif; font-size:14px;width:100%;border-spacing:0;border-collapse:collapse}
                .remittance .title {font-weight:700;font-size:18px;text-align:left}
                .remittance .sub-title {text-align:left}
                .remittance tr {
                  height:25px;
                  vertical-align: middle;
                }
                .remittance td:first-child {
                  width:400px;
                }
                .remittance td:nth-child(2) {
                  width:20px;
                }
                .remittance td:nth-child(3) {
                  width:150px;
                  text-align:right;
                  padding-right:10px;
                }
                .remittance .header-top {
                  font-weight:bold;
                  border-bottom:1px solid #000;
                }
                .remittance .header-bottom {
                  font-weight:bold;
                  border-top:1px solid #000;
                }
              </style>
              <table class="remittance">
                <thead>
                  <tr>
                    <th colspan="5" class="title">Remittance Summary</th>
                  </tr>
                  <tr>
                    <th colspan="5" class="sub-title">${this.rangeDisplay}</th>
                  </tr>
                  <tr><th colspan="5">&nbsp;</th></tr>
                </thead>
                <tbody>
                  <tr class="header-top">
                    <td>LCBO Remittances:</td>
                    <td></td>
                    <td>$ Amount</td>
                  </tr>
                  <tr>
                    <td>Direct Delivery Sales Report (LCB 2414)</td>
                    <td></td>
                    <td>${this.currency(this.lcb2414.grandTotal.W)}</td>
                  </tr>
                  <tr>
                    <td>Direct Delivery to SOP Sales Report (LCB 2416)</td>
                    <td></td>
                    <td>${this.currency(this.lcb2416.grandTotal.S)}</td>
                  </tr>
                  <tr>
                    <td>Duty Free Sales Report (LCB 2415)</td>
                    <td></td>
                    <td>${this.currency(this.lcb2415.totals.O)}</td>
                  </tr>
                  <tr>
                    <td>Container Deposit Statement (LCB 2413)</td>
                    <td></td>
                    <td>${this.currency(this.lcb2413.total.depositValue)}</td>
                  </tr>
                  <tr class="header-bottom">
                    <td>Total LCBO Payment</td>
                    <td></td>
                    <td>${this.currency(this.totalPayment)}</td>
                  </tr>

                  <tr><td colspan="3"/></tr>
                  <tr class="header-top">
                    <td>HST Remittance:</td>
                    <td></td>
                    <td>$ Amount</td>
                  </tr>
                  <tr>
                    <td><strong>DD to Licensees (LCB 2414):</strong></td>
                  </tr>
                  <tr>
                    <td>HST Remitted on LCBO's Sales administered by Distillery</td>
                    <td></td>
                    <td>${this.currency(this.lcb2414.grandTotal.Q)}</td>
                  </tr>
                  <tr>
                    <td>HST Collected on Distillery's Deemed Wholesale Sale to LCBO</td>
                    <td></td>
                    <td>${this.currency(this.lcb2414.grandTotal.R)}</td>
                  </tr>
                  <tr>
                    <td><strong>DD to SOP Holders (LCB 2416):</strong></td>
                  </tr>
                  <tr>
                    <td>HST Remitted on LCBO's Sales administered by Distillery</td>
                    <td></td>
                    <td>${this.currency(this.lcb2416.grandTotal.N)}</td>
                  </tr>
                  <tr>
                    <td>HST Collected on Distillery's Deemed Wholesale Sale to LCBO</td>
                    <td></td>
                    <td>${this.currency(this.lcb2416.grandTotal.O)}</td>
                  </tr>
                  <tr class="header-bottom">
                    <td>Total HST Remittance (included within Total LCBO Payment above)</td>
                    <td></td>
                    <td>${this.currency(this.lcb2414.grandTotal.Q-this.lcb2414.grandTotal.R + this.lcb2416.grandTotal.N-this.lcb2416.grandTotal.O)}</td>
                  </tr>
                </tbody>
              </table>`
    },

    number(value, precision) {
      return this.$number(value, precision);
    },
    currency(value) {
      return this.$currency(value);
    },

    async print() {
      if (this.view === 0)
        this.printHtml('LCB2413',`${this.lcb2413Report()}`);
      else if (this.view === 1) {
        this.printHtml('LCB2414',`${this.lcb2414Report(true,false)}
                       <p style="page-break-before: always"/>
                      ${this.lcb2414Report(false,true)}`);
      }
      else if (this.view === 2) {
        this.printHtml('LDB2415',`${this.lcb2415Report(true,false)}
                        <p style="page-break-before: always"/>
                        ${this.lcb2415Report(false,true)}`);
      }
      else if (this.view === 3) {
        this.printHtml('LCB2416',`${this.lcb2416Report(true,false)}
                       <p style="page-break-before: always"/>
                      ${this.lcb2416Report(false,true)}`);
      }
      else if (this.view === 4)
        this.printHtml('LCBO Remittance Summary',`${this.remittanceSummary()}`);
    },

    exportCSV() {
      const {lines,title} = [this.lcb2413CSV,this.lcb2414CSV,this.lcb2415CSV,this.lcb2416CSV,this.remittanceSummaryCSV][this.view]();

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, `${title}.csv`);
    },
    lcb2413CSV() {
      const lines = [ `"${this.$store.getters.company.name}",,`,
                      `"Distillery Container Deposit Statement",,`,
                      `"${this.rangeDisplay}",,`,
                      `"Deposits Collected by Channel","Containers","Deposit Value"`,
                      `"Less than or equal to 100 ml ($0.00 deposit)",,`,
                      `"Other (e.g. drs, promotional, tastings)","${this.lcb2413.le100.other.containers}","${this.lcb2413.le100.other.depositValue}"`,
                      `"Less: Refilled Containers","0","0"`,
                      `"Subtotal","${this.lcb2413.le100.subTotal.containers}","${this.lcb2413.le100.subTotal.depositValue}"`,
                      `"Greater than 100 ml and less than or equal to 630 ml ($0.10 deposit)",,`,
                      `"Other (e.g. drs, promotional, tastings)","${this.lcb2413.le630.other.containers}","${this.lcb2413.le630.other.depositValue}"`,
                      `"Less: Refilled Containers","0","0"`,
                      `"Subtotal","${this.lcb2413.le630.subTotal.containers}","${this.lcb2413.le630.subTotal.depositValue}"`,
                      `"Greater than 630 ml ($0.20 deposit)",,`,
                      `"Other (e.g. drs, promotional, tastings)","${this.lcb2413.gt630.other.containers}","${this.lcb2413.gt630.other.depositValue}"`,
                      `"Less: Refilled Containers","0","0"`,
                      `"Subtotal","${this.lcb2413.gt630.subTotal.containers}","${this.lcb2413.gt630.subTotal.depositValue}"`,
                      `"Total","${this.lcb2413.total.containers}","${this.lcb2413.total.depositValue}"`
                    ];
      return {lines, title:'LCB2413'};
    },
    lcb2414CSV() {
      const lines = [ `"${this.$store.getters.company.name}",,,,,,,,,,,,,,,,,,,,,,`,
                      `"Distillery Direct Delivery Sales Report",,,,,,,,,,,,,,,,,,,,,,`,
                      `"Summary of Spirit Sales to Licensees on behalf of the LCBO",,,,,,,,,,,,,,,,,,,,,,`,
                      `"${this.rangeDisplay}",,,,,,,,,,,,,,,,,,,,,,`,
                      `"Grand total",,,,,"${this.lcb2414['grandTotal'].F}",,,"${this.lcb2414['grandTotal'].I}","${this.lcb2414['grandTotal'].J}",,"${this.number(this.lcb2414['grandTotal'].L,2)}","${this.number(this.lcb2414['grandTotal'].M,2)}","${this.number(this.lcb2414['grandTotal'].N,2)}","${this.number(this.lcb2414['grandTotal'].O,2)}","${this.number(this.lcb2414['grandTotal'].P,2)}","${this.number(this.lcb2414['grandTotal'].Q,2)}","${this.number(this.lcb2414['grandTotal'].R,2)}","${this.number(this.lcb2414['grandTotal'].S,2)}","${this.number(this.lcb2414['grandTotal'].T,2)}","${this.number(this.lcb2414['grandTotal'].U,2)}","${this.number(this.lcb2414['grandTotal'].V,2)}","${this.number(this.lcb2414['grandTotal'].W,2)}"`,
                      `"Licensees Subtotal",,,,,"${this.lcb2414['licenseesSubTotal'].F}",,,"${this.lcb2414['licenseesSubTotal'].I}","${this.lcb2414['licenseesSubTotal'].J}",,"${this.number(this.lcb2414['licenseesSubTotal'].L,2)}","${this.number(this.lcb2414['licenseesSubTotal'].M,2)}","${this.number(this.lcb2414['licenseesSubTotal'].N,2)}","${this.number(this.lcb2414['licenseesSubTotal'].O,2)}","${this.number(this.lcb2414['licenseesSubTotal'].P,2)}","${this.number(this.lcb2414['licenseesSubTotal'].Q,2)}","${this.number(this.lcb2414['licenseesSubTotal'].R,2)}","${this.number(this.lcb2414['licenseesSubTotal'].S,2)}","${this.number(this.lcb2414['licenseesSubTotal'].T,2)}","${this.number(this.lcb2414['licenseesSubTotal'].U,2)}","${this.number(this.lcb2414['licenseesSubTotal'].V,2)}","${this.number(this.lcb2414['licenseesSubTotal'].W,2)}"`,
                      `"Tied-House Subtotal",,,,,"${this.lcb2414['tiedHouseSubTotal'].F}",,,"${this.lcb2414['tiedHouseSubTotal'].I}","${this.lcb2414['tiedHouseSubTotal'].J}",,"${this.number(this.lcb2414['tiedHouseSubTotal'].L,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].M,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].N,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].O,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].P,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].Q,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].R,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].S,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].T,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].U,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].V,2)}","${this.number(this.lcb2414['tiedHouseSubTotal'].W,2)}"`,
                      `"By the Glass Subtotal",,,,,"${this.lcb2414['byTheGlassSubTotal'].F}",,,"${this.lcb2414['byTheGlassSubTotal'].I}","${this.lcb2414['byTheGlassSubTotal'].J}",,"${this.number(this.lcb2414['byTheGlassSubTotal'].L,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].M,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].N,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].O,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].P,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].Q,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].R,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].S,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].T,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].U,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].V,2)}","${this.number(this.lcb2414['byTheGlassSubTotal'].W,2)}"`,
                      `"(A)","(B)","(C)","(D)","(E)","(F)","(G)","(H)","(I)","(J)","(K)","(L)","(M)","(N)","(O)","(P)","(Q)","(R)","(S)","(T)","(U)","(V)","(W)"`,
                      `"Product Description","Product Alcohol %","Product Category","Licensee Type","Bottle Size (L)","Units Sold","# of Bottles per Unit (Req'd for Product Category 'C')","Basic Price per Unit (Retail price less HST and Deposit)","Total # of Bottles","Total litres","Landed Cost","Cost of Goods Sold to LCBO","Total Basic Sales","5% Discount","6% Licensee Markup","Net Sales","13% HST on Sales","13% HST on COGS","Refundable Container Deposit","Total Collected","Rebate","Supplier Portion (excluding container deposit)","Total Remittance (excluding container deposit)"`
                    ];
      for (const line of this.lcb2414.lines)
        lines.push(`"${line.A}","${line.B}","${line.C}","${line.D}","${line.E}","${line.F}","${line.G||''}","${this.number(line.H,2)}","${line.I}","${this.number(line.J,3)}","${this.number(line.K,2)}","${this.number(line.L,2)}","${this.number(line.M,2)}","${this.number(line.N,2)}","${this.number(line.O,2)}","${this.number(line.P,2)}","${this.number(line.Q,2)}","${this.number(line.R,2)}","${this.number(line.S,2)}","${this.number(line.T,2)}","${this.number(line.U,2)}","${this.number(line.V,2)}","${this.number(line.W,2)}"`);

      return {lines, title:'LCB2414'};
    },
    lcb2415CSV() {
      const lines = [ `"${this.$store.getters.company.name}",,,,,,,,,,,,,,`,
                      `"Distillery Duty Free Sales Report",,,,,,,,,,,,,,`,
                      `"Summary of Ontario spirit sales made to Duty Free Operators on behalf of the LCBO",,,,,,,,,,,,,,`,
                      `"${this.rangeDisplay}",,,,,,,,,,,,,,`,
                      `,,,"Total",,,"${this.lcb2415.totals.G}","${this.lcb2415.totals.H}",,"${this.number(this.lcb2415.totals.J,3)}","${this.number(this.lcb2415.totals.K,2)}","${this.number(this.lcb2415.totals.L,2)}","${this.number(this.lcb2415.totals.M,2)}","${this.number(this.lcb2415.totals.N,2)}","${this.number(this.lcb2415.totals.O,2)}"`,
                      `"(A)","(B)","(C)","(D)","(E)","(F)","(G)","(H)","(I)","(J)","(K)","(L)","(M)","(N)","(O)"`,
                      `"Product Description","Product #","Product Category","Duty Free Operator #","Bottle Size (L)","Number of Bottles per Selling Unit","Selling Units per Case","Number of Cases Sold","Cost per Selling Unit","Total litres","Cost of Goods Sold","LCBO  Mark-up","Total Collected","Unassigned","Total Remittance"`
                    ];
      for (const line of this.lcb2415.lines)
        lines.push(`"${line.A}","${line.B}","${line.C}","${line.D}","${line.E}","${line.F}","${line.G}","${line.H}","${this.number(line.I,4)}","${this.number(line.J,3)}","${this.number(line.K,2)}","${this.number(line.L,2)}","${this.number(line.M,2)}","${this.number(line.N,2)}","${this.number(line.O,2)}"`);

      return {lines, title:'LCB2415'};
    },
    lcb2416CSV() {
      const lines = [ `"${this.$store.getters.company.name}",,,,,,,,,,,,,,,,,,`,
                      `"Distillery Direct Delivery to SOP Sales Report",,,,,,,,,,,,,,,,,,`,
                      `"Summary of Spirit Sales to Special Occasion Permit (SOP) Holders on behalf of the LCBO",,,,,,,,,,,,,,,,,,`,
                      `"${this.rangeDisplay}",,,,,,,,,,,,,,,,,,`,
                      `"Grand total",,,,,"${this.lcb2416['grandTotal'].F}",,,"${this.lcb2416['grandTotal'].I}","${this.lcb2416['grandTotal'].J}",,"${this.number(this.lcb2416['grandTotal'].L,2)}","${this.number(this.lcb2416['grandTotal'].M,2)}","${this.number(this.lcb2416['grandTotal'].N,2)}","${this.number(this.lcb2416['grandTotal'].O,2)}","${this.number(this.lcb2416['grandTotal'].P,2)}","${this.number(this.lcb2416['grandTotal'].Q,2)}","${this.number(this.lcb2416['grandTotal'].R,2)}","${this.number(this.lcb2416['grandTotal'].S,2)}"}"`,
                      `"(A)","(B)","(C)","(D)","(E)","(F)","(G)","(H)","(I)","(J)","(K)","(L)","(M)","(N)","(O)","(P)","(Q)","(R)","(S)"`,
                      `"Product Description","Product Alcohol %","Product Category","SOP License #","Bottle Size (L)","Units Sold","# of Bottles per Unit (Req'd for Product Category 'C')","Basic Price per Unit (Retail price less HST and Deposit)","Total # of Bottles","Total litres","Landed Cost","Cost of Goods Sold to LCBO","Net Sales","13% HST on Sales","13% HST on COGS","Refundable Container Deposit","Total Collected","Supplier Portion (excluding container deposit)","Total Remittance (excluding container deposit)"`
                    ];
      for (const line of this.lcb2416.lines)
        lines.push(`"${line.A}","${line.B}","${line.C}","${line.D}","${line.E}","${line.F}","${line.G||''}","${this.number(line.H,2)}","${line.I}","${this.number(line.J,3)}","${this.number(line.K,2)}","${this.number(line.L,2)}","${this.number(line.M,2)}","${this.number(line.N,2)}","${this.number(line.O,2)}","${this.number(line.P,2)}","${this.number(line.Q,2)}","${this.number(line.R,2)}","${this.number(line.S,2)}"`);

      return {lines, title:'LCB2416'};
    },
    remittanceSummaryCSV() {
      const lines = [ `"${this.$store.getters.company.name}",`,
                      `"Remittance Summary",`,
                      `"${this.rangeDisplay}",`,
                      `"LCBO Remittances:","$ Amount"`,
                      `"Direct Delivery Sales Report (LCB 2414)","${this.number(this.lcb2414.grandTotal.W,2)}"`,
                      `"Direct Delivery to SOP Sales Report (LCB 2416)","${this.number(this.lcb2416.grandTotal.S,2)}"`,
                      `"Duty Free Sales Report (LCB 2415)","${this.number(this.lcb2415.totals.O,2)}"`,
                      `"Container Deposit Statement (LCB 2413)","${this.number(this.lcb2413.total.depositValue,2)}"`,
                      `"Total LCBO Payment","${this.number(this.totalPayment,2)}"`
                    ];
      return {lines, title:'Remittance Summary'};
    }

  }
}
</script>